import React, { useState, useEffect, useCallback } from "react";
import { Button, Table, Segment, Header, Label, Loader, Message } from "semantic-ui-react";
import SmallConfirmPopup from "../../../../../../../components/SmallConfirmPopup";
import apis from "../../../../../../../apis";
import { PRODUCT_NAMES_MAPPING } from "../../../../routers/Products/constants/productNames";
import toastr from "toastr";
import moment from "moment";

const ZendeskInstanceMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingProducts, setUpdatingProducts] = useState(new Set());

  const isEntryllcConfigured = instances.some(
    (inst) => inst.name === "entryllc" && inst.configured,
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ data: mappingsData }, { data: instancesData }] =
        await Promise.all([
          apis.configuration.zendeskConfig.getInstanceMappings(),
          apis.configuration.zendeskConfig.getInstances(),
        ]);
      setMappings(mappingsData);
      setInstances(instancesData);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to load data",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = useCallback(
    async (product, currentInstance) => {
      const newInstance =
        currentInstance === "travelassist" ? "entryllc" : "travelassist";

      setUpdatingProducts((prev) => new Set(prev).add(product));
      try {
        const { data: updated } =
          await apis.configuration.zendeskConfig.updateInstanceMapping(
            product,
            newInstance,
          );
        setMappings((prev) =>
          prev.map((m) => (m.product === product ? { ...m, ...updated } : m)),
        );
        const displayName = PRODUCT_NAMES_MAPPING[product] || product;
        toastr.success(`${displayName} switched to ${newInstance}`);
      } catch (err) {
        toastr.error(
          err.response?.data?.error || "Failed to update instance mapping",
        );
      } finally {
        setUpdatingProducts((prev) => {
          const next = new Set(prev);
          next.delete(product);
          return next;
        });
      }
    },
    [],
  );

  if (loading) {
    return <Loader active />;
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>Failed to load instance mappings</Message.Header>
        <p>{error}</p>
        <Button onClick={fetchData} primary>
          Retry
        </Button>
      </Message>
    );
  }

  return (
    <Segment.Group>
      <Segment className="tableTitle adminTable" inverted>
        <Header size="large" floated="left">
          Zendesk Instance Mapping
        </Header>
      </Segment>
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Product</Table.HeaderCell>
            <Table.HeaderCell>Instance</Table.HeaderCell>
            <Table.HeaderCell>Migrated At</Table.HeaderCell>
            <Table.HeaderCell>Action</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {mappings.map((mapping) => {
            const displayName =
              PRODUCT_NAMES_MAPPING[mapping.product] || mapping.product;
            const isOnTravelassist =
              mapping.instanceName === "travelassist";
            const newInstance = isOnTravelassist
              ? "entryllc"
              : "travelassist";
            const isUpdating = updatingProducts.has(mapping.product);
            const isDisabled =
              isUpdating ||
              (newInstance === "entryllc" && !isEntryllcConfigured);

            return (
              <Table.Row key={mapping.product}>
                <Table.Cell>{displayName}</Table.Cell>
                <Table.Cell>
                  <Label color={isOnTravelassist ? "green" : "blue"}>
                    {mapping.instanceName}
                  </Label>
                </Table.Cell>
                <Table.Cell>
                  {mapping.migratedAt
                    ? moment(mapping.migratedAt).format("YYYY-MM-DD HH:mm")
                    : "--"}
                </Table.Cell>
                <Table.Cell>
                  <SmallConfirmPopup
                    header={`Switch ${displayName} to ${newInstance}?`}
                    content={
                      <Button
                        positive
                        floated="right"
                        content="Switch"
                      />
                    }
                    callback={() =>
                      handleToggle(mapping.product, mapping.instanceName)
                    }
                  >
                    <Button
                      size="small"
                      color={isOnTravelassist ? "blue" : "green"}
                      loading={isUpdating}
                      disabled={isDisabled}
                    >
                      Switch to {newInstance}
                    </Button>
                  </SmallConfirmPopup>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      {!isEntryllcConfigured && (
        <Message warning>
          <Message.Header>Entry LLC instance not configured</Message.Header>
          <p>
            Set ENTRY_ZENDESK_API_EMAIL and ENTRY_ZENDESK_API_TOKEN environment
            variables to enable switching products to entryllc.
          </p>
        </Message>
      )}
    </Segment.Group>
  );
};

export default ZendeskInstanceMapping;
